/**
 * Platform API Constants
 * All platform-level API endpoint definitions
 */

export const PLATFORM_API_ENDPOINTS = {
  BASE: '/platform',
  DASHBOARD: '/platform/dashboard',
  ANALYTICS: {
    OVERVIEW: '/platform/analytics/overview',
    ORGANIZATIONS_STATS: '/platform/analytics/organizations/stats',
    REVENUE_SUMMARY: '/platform/analytics/revenue/summary',
    SYSTEM_HEALTH: '/platform/analytics/system-health',
    USER_GROWTH: '/platform/analytics/user-growth',
    REVENUE: '/platform/analytics/revenue',
  },
  ORGANIZATIONS: {
    LIST: '/platform/organizations',
    CREATE: '/platform/organizations',
    GET: (id: string) => `/platform/organizations/${id}`,
    UPDATE: (id: string) => `/platform/organizations/${id}`,
    DELETE: (id: string) => `/platform/organizations/${id}`,
    BRANCHES: (id: string) => `/platform/organizations/${id}/branches`,
  },
  USERS: {
    LIST: '/platform/users',
    CREATE: '/platform/users',
    GET: (id: string) => `/platform/users/${id}`,
    UPDATE: (id: string) => `/platform/users/${id}`,
    DELETE: (id: string) => `/platform/users/${id}`,
  },
  GROUPS: {
    LIST: '/permissions/groups',
    CREATE: '/permissions/groups',
    GET: (id: string) => `/permissions/groups/${id}`,
    UPDATE: (id: string) => `/permissions/groups/${id}`,
    UPDATE_ROLES: (id: string) => `/permissions/groups/${id}/roles`,
    DELETE: (id: string) => `/permissions/groups/${id}`,
  },
  USER_ACCESS: {
    GET_GROUP_USERS: (groupId: string) => `/user-access/groups/${groupId}/users`,
    UPDATE_GROUP_USERS: (groupId: string) => `/user-access/groups/${groupId}/users`,
  },
  PERMISSIONS: {
    /** DB-backed catalog snapshot (Redis-cached on server). */
    CATALOG: '/permissions/catalog',
    ROLES: {
      LIST: '/permissions/roles',
      GET: (id: string) => `/permissions/roles/${id}`,
      CREATE: '/permissions/roles',
      UPDATE: (id: string) => `/permissions/roles/${id}`,
      DELETE: (id: string) => `/permissions/roles/${id}`,
      SYNC_TO_SCOPE: (id: string) => `/permissions/roles/${id}/sync-to-scope`,
    },
    LIST: '/permissions',
    GET_BY_SCOPE: '/permissions/get-all-or-scope-permissions',
  },
  SETTINGS: {
    LIST: '/settings-management/get-all-settings-with-filters',
    /** Maintenance runtime only (any auth context); no other platform settings. */
    MAINTENANCE_RUNTIME: '/settings-management/maintenance-runtime',
    /** Public branding (no auth required) - logo, favicon, brand name */
    PUBLIC_BRANDING: '/settings-management/public/branding',
    GET_BY_KEY: (idOrKey: string) => `/settings-management/get-setting-by-id/${idOrKey}`,
    SAVE_EDITED: '/settings-management/save-edited',
    BULK_UPDATE: '/settings-management/bulk-update-settings',
    CATEGORIES: '/settings-management/get-settings-metadata',
    TEMPLATES: (scope: string) => `/settings-management/templates/${scope}`,
    STATS: '/settings-management/health',
    CREATE_DEFINITION: '/settings-management/create-setting',
    UPDATE_DEFINITION: (id: string) => `/settings-management/update-setting-by-id/${id}`,
    DELETE_DEFINITION: (id: string) => `/settings-management/delete-setting-by-id/${id}`,
    TOGGLE_STATUS: (id: string) => `/settings-management/update-setting-by-id/${id}`,
    /** Platform user (me) settings - platform context only */
    USER_SETTINGS: {
      GET: '/settings-management/users/me/settings',
      UPDATE: '/settings-management/users/me/settings',
    },
  },
  THEMES: {
    LIST: '/platform/themes',
    STATS: '/platform/themes/stats',
    /** Public default theme (no auth) — landing / marketing shells use light config */
    PUBLIC: '/platform/themes/public',
    GET_BY_ID: (id: string) => `/platform/themes/${id}`,
    GET_CURRENT: '/platform/themes/current',
    CREATE: '/platform/themes',
    UPDATE: (id: string) => `/platform/themes/${id}`,
    DELETE: (id: string) => `/platform/themes/${id}`,
    SET_DEFAULT: (id: string) => `/platform/themes/${id}/set-default`,
    RESET_DEFAULT: (id: string) => `/platform/themes/${id}/reset-default`,
  },
  FONT_SIZES: {
    LIST: '/platform/font-sizes',
    STATS: '/platform/font-sizes/stats',
    GET_BY_ID: (fontSizeId: string) => `/platform/font-sizes/${fontSizeId}`,
    GET_CURRENT: '/platform/font-sizes/current',
    CREATE: '/platform/font-sizes',
    UPDATE: (fontSizeId: string) => `/platform/font-sizes/${fontSizeId}`,
    DELETE: (fontSizeId: string) => `/platform/font-sizes/${fontSizeId}`,
    SET_DEFAULT: (fontSizeId: string) => `/platform/font-sizes/${fontSizeId}/set-default`,
  },
  FEATURES: {
    LIST: '/permissions/features',
    GET_BY_ID: (featureId: string) => `/permissions/features/${featureId}`,
    CREATE: '/permissions/create-feature',
    UPDATE: (featureId: string) => `/permissions/features/${featureId}`,
    DELETE: (featureId: string) => `/permissions/features/${featureId}`,
    CREATE_PERMISSION: (featureId: string) => `/permissions/features/${featureId}/permissions`,
    GET_BY_SCOPE: '/permissions/get-all-or-scope-permissions',
  },
  /** Update/delete a single permission by ID (platform permission management) */
  PERMISSION_BY_ID: (permissionId: string) => `/permissions/${permissionId}`,
  JOBS: {
    LIST: '/platform/jobs',
    GET_BY_ID: (id: string) => `/platform/jobs/${id}`,
    RUNS: (id: string) => `/platform/jobs/${id}/runs`,
    TOGGLE: (id: string) => `/platform/jobs/${id}/toggle`,
    RUN: (id: string) => `/platform/jobs/${id}/run`,
    UPDATE_SCHEDULE: (id: string) => `/platform/jobs/${id}/schedule`,
  },
  PLANS: {
    LIST: '/super-admin/plans',
    GET_BY_ID: (planId: string) => `/super-admin/plans/${planId}`,
    CREATE: '/super-admin/plans',
    UPDATE: (planId: string) => `/super-admin/plans/${planId}`,
    DELETE: (planId: string) => `/super-admin/plans/${planId}`,
    TOGGLE_STATUS: (planId: string) => `/super-admin/plans/${planId}`,
    GROUPED: '/super-admin/plans',
    STATISTICS: '/super-admin/plans',
  },
  MENUS: {
    LIST: '/menu-management/main-menus',
    GET_WITH_SUBMENUS: (menuId: string) => `/menu-management/menus/${menuId}/with-submenus`,
    DELETE: (menuId: string) => `/menu-management/main-menus/${menuId}`,
    SAVE_WITH_SUBMENUS: (menuId?: string) => menuId 
      ? `/menu-management/menus/${menuId}/save-all`
      : '/menu-management/menus/save-all',
    SUBMENUS: {
      LIST: '/menu-management/sub-menus',
      GET_BY_ID: (submenuId: string) => `/menu-management/sub-menus/${submenuId}`,
    },
    FEATURE_FLAGS: {
      BULK_UPDATE: '/menu-management/feature-flags/bulk-update',
    },
  },
  CAROUSELS: {
    LIST: '/carousel-management/get-main-carousels',
    GET_BY_ID: (carouselId: string) => `/carousel-management/get-carousel-by-id/${carouselId}`,
    GET_WITH_ITEMS: (carouselId: string) => `/carousel-management/carousels/${carouselId}/with-items`,
    CREATE: '/carousel-management/create-new-carousel',
    UPDATE: (carouselId: string) => `/carousel-management/update-carousel/${carouselId}`,
    DELETE: (carouselId: string) => `/carousel-management/delete-carousel/${carouselId}`,
    SAVE_WITH_ITEMS: (carouselId?: string) => carouselId 
      ? `/carousel-management/carousels/${carouselId}/save-all`
      : '/carousel-management/carousels/save-all',
    ITEMS: {
      CREATE: (carouselId: string) => `/carousel-management/create-carousel-item/${carouselId}`,
      GET_BY_ID: (itemId: string) => `/carousel-management/get-carousel-item-by-id/${itemId}`,
      UPDATE: (itemId: string) => `/carousel-management/update-carousel-item/${itemId}`,
      DELETE: (itemId: string) => `/carousel-management/delete-carousel-item/${itemId}`,
      BULK: (carouselId: string) => `/carousel-management/carousels/${carouselId}/items/bulk`,
    },
    STATS: '/carousel-management/get-carousel-stats',
  },
  COUPONS: {
    LIST: '/super-admin/coupons',
    GET_BY_ID: (couponId: string) => `/super-admin/coupons?id=${couponId}`,
    CREATE: '/super-admin/coupons',
    UPDATE: (couponId: string) => `/super-admin/coupons/${couponId}`,
    DELETE: (couponId: string) => `/super-admin/coupons/${couponId}`,
  },
  FORM_MANAGEMENT: {
    LIST: '/platform/form-management/configs',
    GET_BY_ID: (formConfigId: string) => `/platform/form-management/configs/${formConfigId}`,
    CREATE: '/platform/form-management/configs',
    UPDATE: (formConfigId: string) => `/platform/form-management/configs/${formConfigId}`,
    DELETE: (formConfigId: string) => `/platform/form-management/configs/${formConfigId}`,
    SYNC: (formConfigId: string) => `/platform/form-management/configs/${formConfigId}/sync`,
  },
  DOCUMENT_TEMPLATES: {
    SECTION_REGISTRY: '/platform/document-templates/section-registry',
    FIELD_REGISTRY: (subType: string) =>
      `/platform/document-templates/field-registry?subType=${encodeURIComponent(subType)}`,
    LIST: (subType: string) => `/platform/document-templates?subType=${encodeURIComponent(subType)}`,
    GET_BY_ID: (id: string) => `/platform/document-templates/${id}`,
    CREATE: '/platform/document-templates',
    UPDATE: (id: string) => `/platform/document-templates/${id}`,
    PREVIEW: '/platform/document-templates/preview',
    DUPLICATE: (id: string) => `/platform/document-templates/${id}/duplicate`,
  },
  /** Super Admin Owner only — remote seed / setup templates */
  SEED: {
    DATASET_A: '/super-admin/seed/dataset-a',
    BACKFILL_PROFILES: '/super-admin/seed/backfill-profiles',
    GREEN_VALLEY_STATUS: '/super-admin/seed/green-valley/status',
    GREEN_VALLEY_CLEAR: '/super-admin/seed/green-valley/clear',
    GREEN_VALLEY_RUN: '/super-admin/seed/green-valley/run',
  },
  /** Academic templates seed — scoped to org + branch; never touches billing/subscription */
  SETUP_TEMPLATES: {
    RUN_SEED: '/platform/setup-templates/run-seed',
  },
  /** Pilot Access (allowlist) - Support Services CRUD */
  PILOT_ACCESS: {
    LIST: '/super-admin/pilot-access',
    GET_BY_ID: (id: string) => `/super-admin/pilot-access/${id}`,
    CREATE: '/super-admin/pilot-access',
    UPDATE: (id: string) => `/super-admin/pilot-access/${id}`,
    DELETE: (id: string) => `/super-admin/pilot-access/${id}`,
  },
  BILLING: {
    SUBSCRIPTIONS: {
      LIST: '/platform/monitoring/branches', // Uses branch monitoring with include=subscription
      GET_BY_ID: (subscriptionId: string) => `/super-admin/subscriptions/${subscriptionId}`,
      UPDATE: (subscriptionId: string) => `/super-admin/subscriptions/${subscriptionId}`,
    },
    PILOTS: {
      LIST: '/super-admin/pilots',
      GRANT: '/super-admin/pilots/grant',
      REVOKE: (subscriptionId: string) => `/super-admin/pilots/${subscriptionId}/revoke`,
      EXTEND: (subscriptionId: string) => `/super-admin/pilots/${subscriptionId}/extend`,
    },
    REVENUE_ANALYTICS: '/super-admin/revenue-analytics',
    PAYMENT_LOGS: {
      LIST: '/super-admin/payment-logs',
      GET_BY_ID: (paymentId: string) => `/super-admin/payment-logs/${paymentId}`,
      RETRY: (paymentId: string) => `/super-admin/payment-logs/${paymentId}/retry`,
    },
    INVOICES: {
      LIST: '/super-admin/invoices',
      GET_BY_ID: (invoiceId: string) => `/super-admin/invoices/${invoiceId}`,
      PDF_URL: (invoiceId: string) => `/super-admin/invoices/${invoiceId}/pdf-url`,
      GENERATE_PDF: (invoiceId: string) => `/super-admin/invoices/${invoiceId}/generate-pdf`,
      DOWNLOAD: (invoiceId: string) => `/super-admin/invoices/${invoiceId}/download`,
      RESEND: (invoiceId: string) => `/super-admin/invoices/${invoiceId}/resend`,
    },
    ORGANIZATIONS: {
      LIST: '/super-admin/organizations',
      GET_BY_ID: (orgId: string) => `/super-admin/organizations/${orgId}`,
      BRANCHES: (orgId: string) => `/super-admin/organizations/${orgId}/branches`,
      UPDATE: (orgId: string) => `/super-admin/organizations/${orgId}`,
      ACTIVATE: (orgId: string) => `/super-admin/organizations/${orgId}/activate`,
      SUSPEND: (orgId: string) => `/super-admin/organizations/${orgId}/suspend`,
    },
    PRICING: {
      LIST: '/super-admin/pricing-versions',
      CURRENT: '/super-admin/pricing-versions/current',
      GET_BY_ID: (id: string) => `/super-admin/pricing-versions/${id}`,
      CREATE: '/super-admin/pricing-versions',
      UPDATE: (id: string) => `/super-admin/pricing-versions/${id}`,
    },
  },
}
